import { useState } from "react";
import PropTypes from "prop-types";
import { useQuery } from "react-query";
import api from "api";
import { Loading } from "components/ui";
import { Button } from "semantic-ui-react";
import { formatDate } from "utils";
import { Link } from "react-router-dom";
import useLocalQuery, { Table } from "hooks/use-local-query";
import { MdNavigateNext, MdNavigateBefore } from "react-icons/md";

const CompanyImportHistory = ({ companyId }) => {
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [importTypeFilter, setImportTypeFilter] = useState("");

  const importRecords = useQuery(
    ["import-history", companyId, page, pageSize, importTypeFilter],
    async () => {
      const params = { page, pageSize };
      if (importTypeFilter) params.importType = importTypeFilter;
      const response = await api.get(`/api/importhistory/company/${companyId}`, { params });
      return response.data;
    }
  );

  const { rows, query, queryDispatcher } = useLocalQuery(
    importRecords?.data?.items ?? [],
    { sort: {}, filters: {} }
  );

  const totalPages = importRecords?.data?.totalPages ?? 1;
  const totalCount = importRecords?.data?.totalCount ?? 0;

  return (
    <div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr auto auto 80px",
          flex: 1,
          padding: 20,
          width: "100%",
          alignItems: "center",
          gap: 10,
        }}
      >
        <h1>Import History</h1>
        <div>
          <label style={{ marginRight: 5 }}>Import Type:</label>
          <select
            value={importTypeFilter}
            onChange={(e) => { setImportTypeFilter(e.target.value); setPage(1); }}
          >
            <option value="">All</option>
            <option value="Backorder">Backorder</option>
            <option value="Compliance">Compliance</option>
            <option value="Demographics">Demographics</option>
            <option value="Resupply">Resupply</option>
          </select>
        </div>
        <div>
          <Button
            style={{ marginRight: 100 }}
            onClick={() => importRecords.refetch()}
          >
            Refresh
          </Button>
        </div>
      </div>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
          flex: 1,
        }}
      >
        {importRecords.isFetching && <Loading />}
        <Table
          query={query}
          queryDispatcher={queryDispatcher}
          rows={rows}
          rowKey="id"
          renderFooter={
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span>Total: {totalCount}</span>
              <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                {page > 1 && (
                  <Button icon size="mini" title="Previous Page" onClick={() => setPage(page - 1)}>
                    <MdNavigateBefore size={20} />
                  </Button>
                )}
                <span>Page {page} of {totalPages}</span>
                {page < totalPages && (
                  <Button icon size="mini" title="Next Page" onClick={() => setPage(page + 1)}>
                    <MdNavigateNext size={20} />
                  </Button>
                )}
              </div>
            </div>
          }
          TableRow={(row) => (
            <tr>
              <td>
                <Link to={`/history/${row.id}`}>{row.import_type}</Link>
              </td>
              <td>{row.file_name}</td>
              <td>{row.status}</td>
              <td>{formatDate(row.date_started)}</td>
              <td>{formatDate(row.date_finished)}</td>
              <td>{row.started_by_name}</td>
              <td>{row.created_count}</td>
              <td>{row.updated_count}</td>
              <td>{row.excluded_count}</td>
              <td>{row.error_count}</td>
            </tr>
          )}
          columns={[
            { header: "Import Type", field: "import_type" },
            { header: "File Name", field: "file_name", style: { width: 450 } },
            { header: "Status", field: "status" },
            { header: "Start Time", field: "date_started" },
            { header: "Finish Time", field: "date_finished" },
            { header: "Started by", field: "started_by_name" },
            { header: "Created", field: "created_count" },
            { header: "Updated", field: "updated_count" },
            { header: "Excluded", field: "exclude_count" },
            { header: "Errors", field: "error_count" },
          ]}
        />
      </div>
    </div>
  );
};

export default CompanyImportHistory;

CompanyImportHistory.propTypes = {
  companyId: PropTypes.string.isRequired,
};
